<script setup lang="ts">
import { useSettingStore } from 'pinia-store'
import { Modal } from 'flowbite'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { InstanceOptions, ModalInterface, ModalOptions } from 'flowbite'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode, Navigation } from 'swiper/modules'
import { toQueryUrl } from '@ui/composables'
import { useCookie } from '#imports'

import 'swiper/css'
import 'swiper/css/navigation'

const props = defineProps({
  id: {
    type: String,
    default: 'modal-items-splash',
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  classes: {
    type: String,
    default: '',
    required: false,
  },
  dimension: {
    type: String,
    default: '800x800',
    required: false,
  },
  modelValue: {
    type: Array,
    default: () => [],
    required: true,
  },
  labels: {
    type: Object,
    default: () => {},
    required: false,
  },
  profile: {
    type: Object,
    default: () => {},
    required: false,
  },
  arrowColor: {
    type: String,
    default: 'var(--color-primary)',
    required: false,
  },
})
const emit = defineEmits(['update:modelValue', 'update:show'])
const items = computed(() => props.modelValue)
const useSetting = useSettingStore()
const modal = ref<ModalInterface>({} as ModalInterface)
const bool = useCookie(`has_splash_${useSetting.version}`)

function init(id) {
  let tmpId = 'modal-items-splash'
  if (props?.id)
    tmpId = props.id

  const targetEl = document?.getElementById(tmpId)
  if (!targetEl)
    return

  // options with default values
  const options: ModalOptions = {
    placement: 'center',
    backdrop: 'dynamic',
    backdropClasses:
      'bg-gray-900/80 !dark:bg-gray-900/80 fixed inset-0 z-40 unset-scroll transition duration-300 delay-150 ease-linear',
    closable: true,
    onHide: () => {
      emit('update:show', false)
      bool.value = true
      useSetting.setHasSplashScreen(true)
    },
    onToggle: () => {
    },
  }

  // instance options object
  const instanceOptions: InstanceOptions = {
    id,
    override: true,
  }
  modal.value = new Modal(targetEl, options, instanceOptions)
  if (props.show && modal.value)
    modal.value.show()
}
watch(() => props.show, (cur) => {
  if (cur && modal.value && props.modelValue && props.modelValue.length)
    modal.value.show()
  else if (modal.value)
    modal.value.hide()
})
onMounted(() => {
  if (props && props.id)
    init(props.id)
})
onBeforeUnmount(() => {
  emit('update:modelValue', [])
})
</script>

<template>
  <!-- modal component -->
  <div
    :id="props.id"
    tabindex="-1"
    aria-hidden="true"
    class="modal-animation modal-items-splash fixed left-0 right-0 top-0 z-50 hidden h-[calc(100%-1rem)] max-h-[100vh] w-full overflow-x-hidden overflow-y-hidden p-4 transition duration-300 delay-150 ease-linear md:inset-0"
  >
    <div
      class="modal-body h-full w-full flex flex-col items-center justify-center"
    >
      <div class="relative max-h-full w-full lg:max-w-5xl">
        <!-- Modal header -->
        <div class="modal-header relative top-0 mb-5 w-full lg:px-6">
          <div
            class="w-full flex items-center justify-end"
          >
            <button
              type="button"
              title="close"
              class="text-gray"
              @click="modal.hide()"
            >
              <IClose size="24"/>
              <span class="sr-only">Close modal</span>
            </button>
          </div>
        </div>
        <!-- Modal body -->
        <div class="modal-content relative h-full w-full flex justify-center overflow-hidden">
          <div
            class="mx-auto h-full max-w-screen-md flex flex-col items-center justify-center"
          >
            <div
              v-if="items && items.length > 1"
              :style="{
                '--swiper-navigation-color': props?.arrowColor,
              }"
              class="swiper-navigation-wrapper absolute left-0 right-0 mx-auto h-1/2 max-w-screen-sm flex items-center justify-between lg:max-w-screen-lg"
            >
              <span class="swiper-button-next h-10 w-10"></span>
              <span class="swiper-button-prev h-10 w-10"></span>
            </div>
            <div
              class="section-swiper-splash-image relative mx-auto h-full w-full overflow-hidden text-center"
            >
              <ClientOnly
                fallback-tag="span"
                fallback="Loading splash..."
              >
                <Swiper
                  :navigation="{
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                  }"
                  class="swiper-dialog overflow-visible"
                  :allow-touch-move="items.length > 1"
                  :auto-height="true"
                  :grab-cursor="true"
                  :slides-per-view="1"
                  :space-between="0"
                  :modules="[Navigation, FreeMode]"
                >
                  <SwiperSlide
                    v-for="(item, index) in items"
                    :key="index"
                  >
                    <a
                      v-if="item?.image"
                      rel="noopener"
                      :title="item?.title || 'Offers'"
                      target="_blank"
                      aria-label="link"
                      :href="item?.link"
                      class="transparent relative"
                    >
                      <VueImg
                        :src="toQueryUrl(item.image, props.dimension ? {
                          d: props.dimension,
                        } : undefined)"
                        :is-lazy-load="true"
                        :alt="item?.title || 'Offers'"
                        class="h-full w-full object-cover"
                        :max-height="800"
                      />
                    </a>
                  </SwiperSlide>
                </Swiper>
              </ClientOnly>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import './splash/component.scss';
</style>
